import { Clapperboard } from 'lucide-react';

// Carátula gráfica (diseñada, SIN footage) de la card "Estudio de Video" en la
// pantalla Modos. Espeja el estilo de la card "Crear desde cero": un
// gráfico sobre gradiente navy de marca, con una tagline en la serif de la marca
// (Playfair Display) y una timeline sutil con un playhead que barre para darle
// vida como las cards con video. Ver ADR-526 y el mockup aprobado.
//
// Rellena el slot 3:2 de la card (se monta como hijo `absolute inset-0` del
// contenedor `.relative aspect-[3/2]`, igual que el <video> de los otros modos).

const CLIP = {
  a: {
    background: 'linear-gradient(90deg,#3B63FF,#1644EB)',
    boxShadow: '0 0 10px rgba(48,90,255,.5)',
  },
  b: { background: 'rgba(255,255,255,0.14)' },
  c: { background: 'rgba(255,255,255,0.22)' },
} as const;

const TRACKS: { kind: keyof typeof CLIP; w: string }[][] = [
  [
    { kind: 'a', w: '26%' },
    { kind: 'b', w: '14%' },
    { kind: 'c', w: '30%' },
    { kind: 'b', w: '18%' },
  ],
  [
    { kind: 'b', w: '18%' },
    { kind: 'a', w: '34%' },
    { kind: 'b', w: '12%' },
    { kind: 'c', w: '22%' },
  ],
];

const TIMELINE_MASK = 'linear-gradient(to top, #000 55%, transparent 100%)';

export const EstudioVideoCover = () => (
  <div
    // `isolate`: aísla el stacking context interno (timeline z-[1] + tagline
    // z-[2]) para que esas capas NO se dibujen por encima del botón Generar /
    // del candado que viven como hermanos afuera. Así la carátula se comporta
    // como una sola capa, igual que el <video> de los otros modos.
    className="absolute inset-0 isolate"
    aria-hidden="true"
    style={{
      background:
        'radial-gradient(120% 90% at 22% 8%, rgba(22,68,235,.55) 0%, rgba(22,68,235,0) 55%), linear-gradient(160deg,#0A1250 0%,#050933 45%,#02062B 100%)',
    }}
  >
    {/* Glow secundario abajo-derecha */}
    <div
      className="absolute -right-[15%] -bottom-[30%] w-[60%] h-[80%] rounded-full"
      style={{
        background:
          'radial-gradient(circle, rgba(80,120,255,.35) 0%, rgba(80,120,255,0) 70%)',
      }}
    />

    {/* Ícono + tagline centrados */}
    <div className="absolute inset-0 z-[2] flex flex-col items-center justify-center gap-3.5 px-6 pb-8 text-center">
      <span className="w-[34px] h-[34px] rounded-[10px] inline-flex items-center justify-center bg-white/10 border border-white/[0.16] text-[#DCE4FF]">
        <Clapperboard className="w-[18px] h-[18px]" strokeWidth={2} />
      </span>
      <p className="m-0 font-display italic text-[#EAF0FF] text-[19px] sm:text-[21px] leading-tight text-balance">
        Un estudio de video <span className="text-[#7E9BFF]">con IA</span>
      </p>
    </div>

    {/* Timeline sutil abajo + playhead animado */}
    <div
      className="absolute inset-x-0 bottom-0 z-[1] h-[34%] px-3.5 pb-3 flex flex-col justify-end gap-1.5"
      style={{ maskImage: TIMELINE_MASK, WebkitMaskImage: TIMELINE_MASK }}
    >
      {TRACKS.map((track, ti) => (
        <div key={ti} className="flex gap-1.5 h-[9px]">
          {track.map((clip, ci) => (
            <span
              key={ci}
              className="h-full rounded-[3px]"
              style={{ width: clip.w, ...CLIP[clip.kind] }}
            />
          ))}
        </div>
      ))}
      {/* El "mover" es full-width (left-0 w-full) para que translateX(%) sea
          relativo al ANCHO de la carátula; animar `transform` (no `left`)
          mantiene la animación en el compositor, sin layout por frame. Adentro
          va la línea de 2px + el dot. `pointer-events-none` porque es decorativo. */}
      <span className="absolute top-[14%] bottom-3 left-0 w-full pointer-events-none animate-caratula-playhead">
        <span
          className="absolute inset-y-0 left-0 w-[2px] rounded-full"
          style={{
            background: 'linear-gradient(#CFE0FF,#7E9BFF)',
            boxShadow: '0 0 8px rgba(150,180,255,.8)',
          }}
        >
          <span
            className="absolute -top-[3px] -left-[3px] w-2 h-2 rounded-full"
            style={{ background: '#DCE7FF', boxShadow: '0 0 8px rgba(160,190,255,.9)' }}
          />
        </span>
      </span>
    </div>
  </div>
);

export default EstudioVideoCover;
